import React from 'react'
import { Button } from '@material-ui/core'
import moment from 'moment'
import entityTypes from '../../../enums/entityTypes'
import urlForPost from '../../urlForPost'
import that from '../../translationHelper'
import Thing from '../Thing'
import displayModes from '../../../enums/displayModes'
import Colour from '../../../components/Item/Colour'
import HeadingAndText from '../../../components/Item/HeadingAndText'
import LoadingInline from '../../../components/LoadingInline'
import PostCredits from '../../../components/Item/PostCredits'
import PostTitleConditions from '../../../components/Item/PostTitleConditions'
import PostSubtitle from '../../../components/Item/PostSubtitle'
import FeedPostComment from '../../../components/FeedPostComment'
import urlForUser from '../../urlForUser'
import { hasTags, images, postImages } from './internal'

const forPostMainTag = func => post => func(hasTags(post) ? post.tags[0] : {})
const postDisplayEntity = func => post => func(post)

const postStatNumbers = post => ({
	numItems: (post.kit && post.kit.items && post.kit.items.length) || 0,
	numPeople: post.tags ? post.tags.filter(t => t.type === entityTypes.USER.type).length : 0
})

const tagSummary = (post) => {
	const values = postStatNumbers(post)
	return that.context.translate('{numItems|1 item|# items}, {numPeople|1 person|# people}', values)
}

export const getPostCredits = (withUrl, useFirstTagAsCredit) => (post) => {
	const credit = useFirstTagAsCredit ? forPostMainTag(Thing.brandName)(post) : (post.userName || that.context.translate('Anonymous'))
	let url
	if (withUrl) {
		if (useFirstTagAsCredit) {
			url = `/search/?query=${encodeURIComponent(forPostMainTag(Thing.brandName)(post))}`
		} else {
			url = post.uid ? urlForUser({ uid: post.uid, isActivated: true }) : null
		}
	}

	return <PostCredits entityName={that.context.translate('Post')} credit={credit} url={url} />
}

const isOwned = entityOwnById => forPostMainTag(Thing.isOwned(entityOwnById))
const isWanted = entityWantById => forPostMainTag(Thing.isWanted(entityWantById))
const isKudos = entityKudosById => post => (entityKudosById && entityKudosById.postKudosById && entityKudosById.postKudosById[post.postId] !== undefined ? !!entityKudosById.postKudosById[post.postId] : undefined)

const postBody = post => <FeedPostComment myPost={post} showAddButton={false} />

const itemPostBody = item => (
	<React.Fragment>
		<Colour itemEntity={item} />
		<HeadingAndText heading={item.text} text={item.description} />
	</React.Fragment>
)

const collectionProps = {
	itemTitle: (post) => {
		let title = post.caption
		if (!title && hasTags(post)) {
			title = forPostMainTag(Thing.title)(post)
		}
		return title
	},
	itemSubtitle: getPostCredits(false),
	itemImageContent: images,
	itemHref: urlForPost,
	itemDescription: forPostMainTag(Thing.description),
	itemFollow: forPostMainTag(Thing.follow),
	itemShowOwnWant: postDisplayEntity(Thing.showOwnWant),
	itemShowKudos: postDisplayEntity(Thing.showKudos),
	itemIconName: forPostMainTag(Thing.propertyGetter({
		[entityTypes.KIT.type]: 'blackKit',
		[entityTypes.USER.type]: 'blackUser'
	})),
	itemIconNameFunc: (itemIconName, _item, displayMode) => (displayMode === displayModes.GRID.type ? (itemIconName || '').split('black').join('white') : itemIconName),
	renderAfterItem: ({ item: post, displayMode }) => (displayMode === displayModes.FULL.type &&
		forPostMainTag(Thing.propertyGetter({
			[entityTypes.ITEM.type]: itemPostBody
		}))(post)
	)
}

const getDateFromTimestamp = timestamp => (timestamp && timestamp.toDate ? timestamp.toDate() : new Date(timestamp))
const getPostTitleFromTimestamp = createdTimestamp => moment(getDateFromTimestamp(createdTimestamp)).format('Do MMM YY')

//	Used in the feed
const feedCollectionProps = {
	...collectionProps,
	itemSubtitle: post => <PostSubtitle post={post} />,
	itemImageContent: postImages,
	itemIconName: postDisplayEntity(Thing.propertyGetter({
		[entityTypes.KIT.type]: 'blackKit',
		[entityTypes.POST.type]: 'blackActivity',
		[entityTypes.USER.type]: 'blackUser'
	})),
	itemHref: post => urlForPost(post),
	itemDescription: postDisplayEntity(Thing.description),
	itemFitScoreTesting: (post) => {
		let title = post.caption
		if (!title && hasTags(post)) {
			title = postDisplayEntity(Thing.title)(post)
		}
		return title
	},
	itemTitle: (post) => {
		const url = urlForPost(post)
		return <PostTitleConditions post={post} url={url} />
	},
	renderAfterItem: ({ item: post, displayMode }) => (displayMode === displayModes.FULL.type &&
		postDisplayEntity(Thing.propertyGetter({
			[entityTypes.ITEM.type]: itemPostBody,
			[entityTypes.POST.type]: postBody
		}))(post)
	),
	itemCounts: (post) => {
		const result = []
		const tagCounts = postStatNumbers(post)

		if (tagCounts.numPeople) {
			result.push({ count: tagCounts.numPeople, name: that.context.translate('{count|PERSON|PEOPLE}', { count: tagCounts.numPeople }), type: entityTypes.USER.type })
		}
		if (tagCounts.numItems) {
			result.push({ count: tagCounts.numItems, name: that.context.translate('{count|ITEM|ITEMS}', { count: tagCounts.numItems }), type: entityTypes.ITEM.type })
		}
		return result
	}
}

// TODO: this should be a component
const renderNoPosts = (hasLoaded, action, message) => () => {
	const renderMessage = () => (action ?
		<Button onClick={action}>{message}</Button> :
		<div>{message}</div>
	)
	return (
		<div>
			{hasLoaded ? renderMessage() : <LoadingInline />}
		</div>
	)
}

export default {
	collectionProps,
	feedCollectionProps,
	getPostTitleFromTimestamp,
	renderNoPosts,
	images,
	postImages,
	tagSummary,
	postStatNumbers,
	forPostMainTag,
	isOwned,
	isWanted,
	isKudos
}



// WEBPACK FOOTER //
// ./src/utilities/Data/Post/index.js